import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, Download } from 'lucide-react';
import AdminSidebar from '../../components/AdminSidebar';
import api from '../../utils/api';
import { toast } from 'sonner';

const AdminReports = () => {
  const [revenueData, setRevenueData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchReports();
  }, []);

  const fetchReports = async () => {
    try {
      const response = await api.get('/reports/revenue');
      setRevenueData(response.data);
    } catch (error) {
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  const totalRevenue = revenueData.reduce((sum, item) => sum + item.revenue, 0);
  const totalBookings = revenueData.reduce((sum, item) => sum + item.bookings, 0);

  const handleExport = () => {
    const csv = [
      ['Month', 'Revenue', 'Bookings'],
      ...revenueData.map((item) => [item._id, item.revenue, item.bookings]),
    ]
      .map((row) => row.join(','))
      .join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `revenue-report-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    toast.success('Report exported successfully');
  };

  return (
    <div className="flex" style={{ background: '#F9F8F6' }}>
      <AdminSidebar />
      <div className="admin-content flex-1" data-testid="admin-reports">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1
              className="text-4xl font-bold mb-2"
              style={{ fontFamily: 'Playfair Display, serif', color: '#1A3C34' }}
            >
              Reports
            </h1>
            <p style={{ color: '#64748B' }}>Revenue and booking analytics</p>
          </div>
          <button
            onClick={handleExport}
            className="btn-primary flex items-center gap-2"
            style={{ padding: '0.75rem 1.5rem' }}
            data-testid="export-button"
          >
            <Download size={20} />
            Export CSV
          </button>
        </div>

        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl p-6 shadow-sm"
            data-testid="total-revenue-card"
          >
            <div className="flex items-center gap-4">
              <div
                className="w-14 h-14 rounded-xl flex items-center justify-center"
                style={{ background: '#1A3C3420', color: '#1A3C34' }}
              >
                <TrendingUp size={28} />
              </div>
              <div>
                <p className="text-sm mb-1" style={{ color: '#64748B' }}>
                  Total Revenue
                </p>
                <p className="text-3xl font-bold mono" style={{ color: '#1A3C34' }}>
                  ₹{totalRevenue.toFixed(2)}
                </p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-xl p-6 shadow-sm"
            data-testid="total-bookings-card"
          >
            <div className="flex items-center gap-4">
              <div
                className="w-14 h-14 rounded-xl flex items-center justify-center"
                style={{ background: '#8F9E8B20', color: '#8F9E8B' }}
              >
                <TrendingUp size={28} />
              </div>
              <div>
                <p className="text-sm mb-1" style={{ color: '#64748B' }}>
                  Total Bookings
                </p>
                <p className="text-3xl font-bold mono" style={{ color: '#1A3C34' }}>
                  {totalBookings}
                </p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-xl p-6 shadow-sm"
            data-testid="avg-revenue-card"
          >
            <div className="flex items-center gap-4">
              <div
                className="w-14 h-14 rounded-xl flex items-center justify-center"
                style={{ background: '#C67D6320', color: '#C67D63' }}
              >
                <TrendingUp size={28} />
              </div>
              <div>
                <p className="text-sm mb-1" style={{ color: '#64748B' }}>
                  Avg per Booking
                </p>
                <p className="text-3xl font-bold mono" style={{ color: '#1A3C34' }}>
                  ₹{totalBookings ? (totalRevenue / totalBookings).toFixed(2) : '0.00'}
                </p>
              </div>
            </div>
          </motion.div>
        </div>

        {loading ? (
          <div className="text-center py-20">
            <p style={{ color: '#64748B' }}>Loading reports...</p>
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <div className="p-6 border-b" style={{ borderColor: '#E5E7EB' }}>
              <h2
                className="text-2xl font-bold"
                style={{ fontFamily: 'Playfair Display, serif', color: '#1A3C34' }}
              >
                Monthly Revenue Breakdown
              </h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead style={{ background: '#F9F8F6' }}>
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-medium" style={{ color: '#1A202C' }}>
                      Month
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-medium" style={{ color: '#1A202C' }}>
                      Revenue
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-medium" style={{ color: '#1A202C' }}>
                      Bookings
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-medium" style={{ color: '#1A202C' }}>
                      Avg per Booking
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {revenueData.map((item, index) => (
                    <motion.tr
                      key={item._id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: index * 0.05 }}
                      className="border-t border-gray-100"
                      data-testid={`revenue-row-${index}`}
                    >
                      <td className="px-6 py-4">
                        <span className="font-medium" style={{ color: '#1A202C' }}>
                          {new Date(item._id + '-01').toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                          })}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="font-bold mono" style={{ color: '#1A3C34' }}>
                          ₹{item.revenue.toFixed(2)}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="font-medium" style={{ color: '#64748B' }}>
                          {item.bookings}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="font-medium mono" style={{ color: '#64748B' }}>
                          ₹{(item.revenue / item.bookings).toFixed(2)}
                        </span>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
            {revenueData.length === 0 && (
              <div className="text-center py-20">
                <p style={{ color: '#64748B' }}>No revenue data available</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminReports;